import { type DocNode } from '@atlaskit/adf-schema';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { addUFOCustomData } from '@atlaskit/react-ufo/custom-data';
import { getRovoParams, updatePageRovoParams } from '@atlaskit/rovo-triggers/params';
import { encodeAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import { StoreActionLoggableName } from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { getLastMessageSentAt } from '../last-message-sent/getLastMessageSentAt';
import {
	getPersistedConversationActivity,
	getPersistedRecentConversationId,
	isPersistedConversationActivityRestorable,
	isPersistedConversationRestorationEnabled,
	persistRecentConversation,
} from '../recent-conversation-persistence/recentConversationPersistence';

import { createConversationOptimistically } from './createConversationOptimistically';
import { createSafeAction, type SafeAction } from './createSafeAction';
import { getTimeoutBeforeExpiration } from './getTimeoutBeforeExpiration';
import { isEmptyConversation } from './isEmptyConversation';
import { isValidAdfJsonString } from './isValidAdfJsonString';
import { sendMessage } from './sendMessage';
import { setSelectedConversation } from './setSelectedConversation';
import { shouldPreserveSpaceChatOnHydrate } from './shouldPreserveSpaceChatOnHydrate';
import type { ChatStoreAction } from './types';

/*
 * This action runs on initial load to hydrate this in-memory store (chatStore)
 * with data from either URL parameters or LocalStorage. URL parameters take precedence.
 *
 * Setting the currentConversationId (using setSelectedConversation or createConversationOptimistically)
 * may implicitly trigger fetching of conversation messages.
 *
 * Any change to currentConversationId will trigger a useEffect in ChatHistory,
 * which then executes the `fetchConversation` action.
 */
type HydrateConversationWithExternalParamsArgs = { autoSelectConversation: boolean };

export const hydrateConversationWithExternalParams: SafeAction<
	[HydrateConversationWithExternalParamsArgs]
> = createSafeAction(
	StoreActionLoggableName.hydrateConversation,
	({ autoSelectConversation }: HydrateConversationWithExternalParamsArgs): ChatStoreAction =>
		async (
			{ getState, dispatch },
			{
				siteId,
				experienceTracker,
				setChatStatus,
				initChatInitiator,
				insertHighlightActionsEditorPlaceholder,
				setStagingAreaMode,
				setMessageIdSelectedForPreview,
				getStoreUserMessagesPreference,
			},
		) => {
			experienceTracker.start({
				id: ExperienceName.HYDRATE_CONVERSATION,
				name: ExperienceName.HYDRATE_CONVERSATION,
			});

			const rovoParamsUnsanitized = getRovoParams();
			const rovoParams = Object.fromEntries(
				Object.entries(rovoParamsUnsanitized).filter(([, v]) => v !== 'undefined'),
			) as typeof rovoParamsUnsanitized;

			let initWithParams = autoSelectConversation && rovoParams.pathway === 'chat';
			const { currentConversationId } = getState();

			if (rovoParams.stagingAreaOpen && fg('aifc_create_enabled')) {
				setStagingAreaMode('inline', 'rovoChat');
				updatePageRovoParams({ stagingAreaOpen: undefined });
			}
			if (rovoParams.messageIdSelectedForPreview && fg('aifc_create_enabled')) {
				setMessageIdSelectedForPreview(rovoParams.messageIdSelectedForPreview);
				updatePageRovoParams({ messageIdSelectedForPreview: undefined });

				// Start experience on Home side — the originating tab (e.g. Bitbucket) can't track it cross-tab.
				experienceTracker.start({
					name: ExperienceName.PREVIEW_CONTENT,
					id: rovoParams.messageIdSelectedForPreview,
					timeout: 30000,
					attributes: {
						source: 'hydrateConversationWithExternalParams',
					},
				});
			}

			if (rovoParams.triggerOpen) {
				// if the rovo chat was actually open with url param
				initChatInitiator(rovoParams.initiator);
			}

			// Process params as a priority
			if (initWithParams) {
				if (rovoParams.prompt) {
					// URL prompts are one-shot inputs. Remove the prompt before selecting,
					// creating, or sending so any Appification remount reads a URL with no
					// prompt left to replay.
					updatePageRovoParams({ prompt: undefined, insertPrompt: undefined });
				}

				const localConversation = rovoParams.conversationId
					? getState().conversations[rovoParams.conversationId]
					: undefined;

				if (rovoParams.conversationId && localConversation) {
					dispatch(
						setSelectedConversation({
							conversationId: rovoParams.conversationId,
							sourceAgentId: rovoParams.agentId,
						}),
					);
					// Clear conversationId from URL after consuming it so subsequent chat opens
					// don't reuse it and get stuck in the same conversation.
					updatePageRovoParams({ conversationId: undefined });
				} else {
					dispatch(
						createConversationOptimistically({
							id: rovoParams.conversationId,
							sourceAgentId: rovoParams.agentId,
						}),
					);
				}

				if (rovoParams.prompt) {
					// For cases where the prompt is a json string containing ADF, we need to parse it
					const shouldParse = isValidAdfJsonString(rovoParams.prompt);
					const prompt = shouldParse
						? (JSON.parse(rovoParams.prompt) as DocNode)
						: rovoParams.prompt;
					const urlPromptProvenance = fg('rovo_chat_prompt_url_warning')
						? {
								source: 'url-param' as const,
								conversationId: getState().currentConversationId,
							}
						: undefined;

					if (rovoParams.insertPrompt === 'highlight-action') {
						insertHighlightActionsEditorPlaceholder(prompt as DocNode, urlPromptProvenance);
					} else if (fg('rovo_chat_disable_url_prompt_auto_send')) {
						// Security (VULN-1804729): never auto-send a prompt supplied via the
						// `rovoChatPrompt` URL query param. A crafted link could otherwise inject
						// and one-click execute an arbitrary prompt as the signed-in user
						// (prompt injection / data exfiltration). Instead, pre-fill the prompt
						// into the chat input as an editable draft so the user must explicitly
						// review and send it, keeping a human in the loop.
						insertHighlightActionsEditorPlaceholder(encodeAdf(prompt, false, undefined, true), {
							skipPlaceholderPrompt: true,
							...urlPromptProvenance,
						});
					} else {
						dispatch(
							sendMessage({
								prompt: encodeAdf(prompt, false, undefined, true),
								agentId: rovoParams.agentId,
								// Use the user's preference for storing messages
								// TODO(ai_mate_store_messages_be_driven): remove this local storeUserMessages payload
								// value when BE user settings are fully rolled out; BE overrides it via the capability header.
								storeUserMessages: getStoreUserMessagesPreference?.() ?? false,
								messageSources: {
									interactionSource: 'urlParam',
								},
							}),
						);
					}
				}

				// for UFO performance tracking dimensions
				addUFOCustomData({
					isEmptyConversation: !rovoParams.conversationId,
					hasInitialPrompt: !!rovoParams.prompt,
				});
			} else if (autoSelectConversation) {
				const timeoutBeforeNewChat = getTimeoutBeforeExpiration();
				const isAssociationRestorationEnabled = isPersistedConversationRestorationEnabled();
				// HOT-303866: a since-reverted change stored a stringified JSON object under
				// this key instead of a plain conversation ID. If we detect that corrupt object
				// shape, self-heal by overwriting the key and start a new conversation.
				const getSafeStoredConversationId = (): string => {
					const raw = getPersistedRecentConversationId(siteId);
					if (!raw) {
						return EMPTY_CONVERSATION_ID;
					}
					let isCorruptObject = false;
					try {
						const parsed: unknown = JSON.parse(raw);
						isCorruptObject = typeof parsed === 'object' && parsed !== null;
					} catch {
						// Not JSON — a plain conversation ID string, use it as-is.
					}
					if (isCorruptObject) {
						persistRecentConversation({
							type: 'conversation-selected',
							siteId,
							conversationId: EMPTY_CONVERSATION_ID,
						});
						return EMPTY_CONVERSATION_ID;
					}
					return raw;
				};
				const targetConversationId =
					fg('rovo_chat_safe_conversation_id_from_ls') || isAssociationRestorationEnabled
						? getSafeStoredConversationId()
						: (getPersistedRecentConversationId(siteId) ?? EMPTY_CONVERSATION_ID);

				// Preserve in-memory space chat across container remount.
				// See `shouldPreserveSpaceChatOnHydrate` for full rationale.
				if (
					shouldPreserveSpaceChatOnHydrate(
						currentConversationId,
						getState().conversations[currentConversationId ?? ''],
					)
				) {
					addUFOCustomData({ isEmptyConversation: false });
					setChatStatus('online');
					experienceTracker.succeed({ name: ExperienceName.HYDRATE_CONVERSATION });
					return;
				}

				const hasTargetChanged = targetConversationId !== currentConversationId;
				const isTargetConversationEmpty = isEmptyConversation(targetConversationId);
				const hasLocalTargetConversation = Boolean(getState().conversations[targetConversationId]);

				if (isAssociationRestorationEnabled) {
					const isTargetRestorable = isPersistedConversationActivityRestorable({
						selectedConversationId: targetConversationId,
						activity: getPersistedConversationActivity(siteId),
						now: Date.now(),
						timeout: timeoutBeforeNewChat,
					});

					if (!isTargetRestorable) {
						dispatch(setSelectedConversation({ conversationId: EMPTY_CONVERSATION_ID }));
					} else if (hasTargetChanged && hasLocalTargetConversation) {
						dispatch(setSelectedConversation({ conversationId: targetConversationId }));
					} else if (hasTargetChanged) {
						dispatch(createConversationOptimistically({ id: targetConversationId }));
					}

					addUFOCustomData({ isEmptyConversation: !isTargetRestorable });
				} else {
					const lastMessageSentAt = getLastMessageSentAt();
					const isTargetExpired =
						lastMessageSentAt && Date.now() - lastMessageSentAt >= timeoutBeforeNewChat;

					// start new conversation: if recent expired
					if (isTargetExpired) {
						dispatch(
							setSelectedConversation({
								conversationId: EMPTY_CONVERSATION_ID,
								clearLegacyLastMessageSentAt: true,
							}),
						);
						// start new conversation: if target conversation was set to new (e.g. in another tab)
					} else if (hasTargetChanged && isTargetConversationEmpty) {
						dispatch(setSelectedConversation({ conversationId: EMPTY_CONVERSATION_ID }));
						// select existing conversation: if convo exists in memory
					} else if (hasTargetChanged && hasLocalTargetConversation) {
						dispatch(setSelectedConversation({ conversationId: targetConversationId }));
						// create a new optimistic conversation: renders ChatHistory which triggers API call to fetch messages
					} else if (hasTargetChanged) {
						dispatch(createConversationOptimistically({ id: targetConversationId }));
					}

					// for UFO performance tracking dimensions
					addUFOCustomData({ isEmptyConversation: !isTargetExpired && hasTargetChanged });
				}
			}

			// Set online status later, to ensure that pubsub listener doesn't race condition with store init state
			setChatStatus('online');

			experienceTracker.succeed({ name: ExperienceName.HYDRATE_CONVERSATION });
		},
	{
		experienceName: () => ExperienceName.HYDRATE_CONVERSATION,
	},
);
